import React, { useEffect, useRef, useState } from "react";
import { TextField, Button, Switch, FormControlLabel, FormControl, Grid, Box, InputAdornment,Checkbox } from "@mui/material";
import { useAuth, useSnackbar, useLoading, useNavigation } from "../../Context/Contexts";
import { CustomerViewModel } from "../../Types/Customer/CustomerViewModel";
import CenteredCard from "../../components/Card/CenteredCardLayout";
import ActionMenu, { ActionItem } from "../../components/Card/ActionMenu";
import {
    CustomerAutoComplete, CountryAutoComplete, CurrencyAutoComplete, Dimension1AutoComplete, Dimension2AutoComplete,
    CustomerInvoiceDiscountGroupAutoComplete, CustomerPriceGroupAutoComplete, PaymentTermsAutoComplete, VatbusPostingGroupAutoComplete, SalespersonAutoComplete, ShipmentMethodAutoComplete, LanguageAutoComplete
} from "../../components/AutoComplete/AutoCompletes";
import InfoButton from "../../components/Tooltip/InfoTextField";
import ShippingAgentServiceSelect from "../../components/Shipping/ShippingAgentServiceSelect";
import { useTranslation } from "react-i18next";
import { ToolbarWithSaveHandle } from "../../components/Toolbar/ToolbarWithSave";

const EditCustomerPage: React.FC<{ params?: { [key: string]: any } }> = ({ params }) => {
    const { customerNo = "" } = params || {};
    const { t } = useTranslation();
    const [isLoading, setIsLoading] = useState(false);
    const { showLoading, hideLoading } = useLoading();
    const { authService } = useAuth();
    const { showSnackbar } = useSnackbar();
    const [customer, setCustomer] = useState<CustomerViewModel | null>(null);




    const { goBack, navigate } = useNavigation();
    const [showSettings, setShowSettings] = useState(false);
    const toolbarRef = useRef<ToolbarWithSaveHandle>(null);

    const hasPendingChanges = () => customer?.hasChanges === true;

    const showHasChangesError = () => {
        toolbarRef.current?.showHasChangesError();
    };

    const preventIfUnsaved = () => {
        if (hasPendingChanges()) {
            showHasChangesError();
            return true;
        }
        return false;
    };

    useEffect(() => {
        if (isLoading) showLoading();
        else hideLoading();
    }, [isLoading, showLoading, hideLoading]);

    useEffect(() => {
        if (customerNo) {
            setIsLoading(true);
            authService
                .get<CustomerViewModel>(`/customerApi/GetCustomer?no=${customerNo}`)
                .then((response) =>
                    setCustomer(
                        response
                            ? { ...response, originalValues: { ...response }, hasChanges: false }
                            : null
                    )
                )
                .catch((error: any) => {
                    showSnackbar(`${t("dialog.fetchCustomerError")} : ${error.response?.data ?? error.message}`, "error");
                })
                .finally(() => setIsLoading(false));
        } else {
            const defaultCustomer: CustomerViewModel = {
                name: "",
                name2: "",
                address: "",
                address2: "",
                city: "",
                phoneNo: "",
                blocked: false,
                postCode: "",
                eMail: "",
                contact: "",
                globalDimension1Code: "",
                globalDimension2Code: "",
                pricesIncludingVAT: true,
                xConsumer: false,
                xBusiness: false,
                xTemplate: false,
                xMobileNo: "",
                county: "",
                newCustomer: true,
                originalValues: {},
                hasChanges: false,
            };
            defaultCustomer.originalValues = { ...defaultCustomer };
            setCustomer(defaultCustomer);
        }
    }, [customerNo, authService, setIsLoading]);


    const handleSave = async () => {
        if (!customer) return;
        if (!validate()) return;
        setIsLoading(true);
        try {
            const response = await authService.post<CustomerViewModel>("/customerApi/CreateOrUpdateCustomer", customer);
            if (customer.newCustomer) {
                navigate({ path: "/sales/customer/edit", params: { customerNo : response.no } });
                setIsLoading(false);
                return;
            }
            setCustomer(response ? { ...response, originalValues: { ...response }, hasChanges: false } : null);
            showSnackbar(t("dialog.savedSuccessfully"), "success");
        } catch {
            showSnackbar(t("dialog.saveErrorGeneric"), "error");
        } finally {
            setIsLoading(false);
        }
    };

    const handleAnonymize = async () => {
        if (preventIfUnsaved() || !customer) return;
        const no = customer.no || customerNo;
        if (!no) return;
        if (!window.confirm(t("dialog.areYouSure"))) return;
        setIsLoading(true);
        try {
            await authService.post("/customerApi/AnonymizePersonalData", { customerNo: no });
            showSnackbar(t("dialog.customerAnonymizedQueued"), "success");
        } catch {
            showSnackbar(t("dialog.saveErrorGeneric"), "error");
        } finally {
            setIsLoading(false);
        }
    };




const handleChange = (field: keyof CustomerViewModel, value: any) => {
    setCustomer((prev) => {
        if (!prev) return prev;
        return { ...prev, [field]: value, hasChanges: true };
    });
};

const handleChanges = (changes: Partial<CustomerViewModel>) => {
    setCustomer((prev) => {
        if (!prev) return prev;
        return { ...prev, ...changes, hasChanges: true };
    });
};

const handleCancelChanges = () => {
   // setIsLoading(true);
    try {
        setCustomer((prev) => {
            if (!prev || !prev.originalValues) return prev;
            const original = prev.originalValues;
            const changes: Partial<CustomerViewModel> = {};
            for (const key in original) {
                const typedKey = key as keyof CustomerViewModel;
                const originalValue = original[typedKey];
                const currentValue = prev[typedKey];
                if (originalValue !== currentValue) {
                    (changes as any)[typedKey] = originalValue;
                }
            }
             if (Object.keys(changes).length === 0) return { ...prev, hasChanges: false };
            return { ...prev, ...changes, hasChanges: false };
        });
    } finally {
      //  setIsLoading(false);
    }
};


    const validate = () => {
        if (!customer) return false;
        if (!customer.name || customer.name.trim() === "") {
            showSnackbar(t("help.nameRequired"), "warning");
            return false;
        }

        if (!customer.countryCode || customer.countryCode.trim() === "") {
            showSnackbar(t("help.countryRequired"), "warning");
            return false;
        }
        return true;
    };



    const handleDelete = async () => {
        const isConfirmed = window.confirm(t("dialog.deleteItemConfirm"));
        if (!isConfirmed) return;
        if (!customer) return;
        var newItem = customer.newCustomer ?? false;
        if (newItem) {
            navigate({ path: "/sales/customer/list" });
            return;
        }

        setIsLoading(true);
        try {
            await authService.post(`/customerApi/DeleteCustomer`, customer);
            showSnackbar(t("dialog.deletedSuccessfully"), "success");

            navigate({ path: "/sales/customer/list" });
        } catch (error: any) {
            showSnackbar(`${t("dialog.itemDeleteError")} : ${error.response?.data ?? error.message}`, "error");


        } finally {
            setIsLoading(false);
        }
    };

    const handleCreateCustomer = () => {
        const isConfirmed = window.confirm(t("dialog.areYouSure"));
        if (!isConfirmed) return;
        navigate({ path: "/sales/customer/edit", params: { customerNo: "" } });
    };

    return (
        <>
            <CenteredCard
                ref={toolbarRef}
                title={t("common.edit") + " " + t("common.customer")}
                isLoading={isLoading}
                handleSave={handleSave}
                handleCancel={handleCancelChanges}
                hasChanges={customer?.hasChanges ?? false}
            >
                {customer && (
                    <>
                        <Box sx={{ display: "flex", justifyContent: "flex-start", maxWidth: "fit-content", mb: 2 }}>
                            <ActionMenu actions={[
                                { title: t("common.createNew"), onClick: handleCreateCustomer },
                                { title: t("common.anonymizePersonalData"), onClick: handleAnonymize },
                                { title: t("common.delete"), onClick: handleDelete, color: "error" }
                    
                            ]} />
                        </Box>
                        <Grid container spacing={2} >


                            <Grid item xs={12}>
                                {!customer.newCustomer &&
                                    <TextField
                                    label={t("common.customerNo")}
                                    value={customer.no || ""}
                                        fullWidth
                                        margin="normal"
                                    disabled={!customer.newCustomer}
                                    />
                                }
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    label={t("common.name")}
                                    value={customer.name}
                                    onChange={(e) => handleChange("name", e.target.value)}
                                    fullWidth
                                    margin="normal"
                                    inputProps={{ maxLength: 100 }}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <InfoButton infoText={t("help.customerName")} />
                                            </InputAdornment>
                                        ),
                                    }}
                                    error={(!customer.name || customer.name.trim() === "")}
                                    helperText={
                                        !customer.name?.trim() ? t("help.nameRequired") : ""
                                    }
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    label={t("common.name2")}
                                    value={customer.name2 || ""}
                                    onChange={(e) => handleChange("name2", e.target.value)}
                                    fullWidth
                                    margin="normal"
                                    inputProps={{ maxLength: 100 }}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <InfoButton infoText={t("help.customerNameAlt")} />
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    label={t("common.address")}
                                    value={customer.address || ""}
                                    onChange={(e) => handleChange("address", e.target.value)}
                                    fullWidth
                                    margin="normal"
                                    inputProps={{ maxLength: 100 }}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <InfoButton infoText={t("help.customerAddress")} />
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    label={t("common.address") + " 2"}
                                    value={customer.address2 || ""}
                                    onChange={(e) => handleChange("address2", e.target.value)}
                                    fullWidth
                                    margin="normal"
                                    inputProps={{ maxLength: 100 }}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <InfoButton infoText={t("help.customerAddressAlt")} />
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    label={t("common.postCode")}
                                    value={customer.postCode || ""}
                                    onChange={(e) => handleChange("postCode", e.target.value)}
                                    fullWidth
                                    margin="normal"
                                    inputProps={{ maxLength: 50 }}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    label={t("common.city")}
                                    value={customer.city || ""}
                                    onChange={(e) => handleChange("city", e.target.value)}
                                    fullWidth
                                    margin="normal"
                                    inputProps={{ maxLength: 100 }}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <FormControl fullWidth margin="normal">
                                    <CountryAutoComplete
                                        key={customer.countryCode || null}
                                        valueId={customer.countryCode || null}
                                        onChange={(selected) => handleChange("countryCode", selected?.value || "")}
                                        infoText={t("help.customerCountry")}
                                        selectFirst={true}
                                        
                                    />
                                </FormControl>
                            </Grid>



                            <Grid item xs={12}>
                                <TextField
                                    label={t("common.phoneNo") }
                                    value={customer.phoneNo || ""}
                                    onChange={(e) => handleChange("phoneNo", e.target.value)}
                                    fullWidth
                                    margin="normal"
                                    inputProps={{ maxLength: 100 }}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <InfoButton infoText={t("help.customerPhone")} />
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    label={t("common.email")}
                                    value={customer.eMail || ""}
                                    onChange={(e) => handleChange("eMail", e.target.value)}
                                    fullWidth
                                    margin="normal"
                                    inputProps={{ maxLength: 100 }}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <InfoButton infoText={t("help.customerEvents")} />
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    label={t("common.contact")}
                                    value={customer.contact || ""}
                                    onChange={(e) => handleChange("contact", e.target.value)}
                                    fullWidth
                                    margin="normal"
                                    inputProps={{ maxLength: 100 }}
                                />
                            </Grid>
                            {/*    <Grid item xs={12}>*/}
                            {/*<TextField*/}
                            {/*    label="Global Dimension 1 Code"*/}
                            {/*    value={customer.globalDimension1Code || ""}*/}
                            {/*    onChange={(e) => handleChange("globalDimension1Code", e.target.value)}*/}
                            {/*    fullWidth*/}
                            {/*    margin="normal"*/}
                            {/*        />*/}
                            {/*    </Grid>*/}
                            {/*    <Grid item xs={12}>*/}
                            {/*<TextField*/}
                            {/*    label="Global Dimension 2 Code"*/}
                            {/*    value={customer.globalDimension2Code || ""}*/}
                            {/*    onChange={(e) => handleChange("globalDimension2Code", e.target.value)}*/}
                            {/*    fullWidth*/}
                            {/*    margin="normal"*/}
                            {/*        />*/}
                            {/*</Grid>*/}


                            <Grid item xs={12}>
                                <FormControl fullWidth margin="normal">
                                    <CurrencyAutoComplete
                                        key={customer.currencyCode || null}
                                        valueId={customer.currencyCode || null}
                                        onChange={(selected) => handleChange("currencyCode", selected?.value || "")}
                                        infoText={t("help.customerCurrency")}
                                    />
                                </FormControl>
                            </Grid>
                            <Grid item xs={12}>
                                <FormControl fullWidth margin="normal">
                                    <LanguageAutoComplete
                                        key={customer.languageCode || null}
                                        valueId={customer.languageCode || null}
                                        onChange={(selected) => handleChange("languageCode", selected?.value || "")}
                                        infoText={t("help.customerLanguage")}
                                    />
                                </FormControl>
                            </Grid>

                            <Grid item xs={6} lg={4}>
                                <FormControlLabel
                                    control={<Checkbox checked={!!customer.blocked} onChange={(e) => handleChange("blocked", e.target.checked)} />}
                                    label={t("common.blocked")}


                                />
                            </Grid>
                            <Grid item xs={6} lg={4}>
                                <FormControlLabel
                                    control={<Checkbox checked={!!customer.pricesIncludingVAT} onChange={(e) => handleChange("pricesIncludingVAT", e.target.checked)} />}
                                    label={t("common.pricesInclVat")}
                                />
                            </Grid>
                            <Grid item xs={6} lg={4}>
                                <FormControlLabel
                                    control={<Checkbox checked={!!customer.xConsumer} onChange={(e) => handleChange("xConsumer", e.target.checked)} />}
                                    label={t("common.consumer")}
                                />
                            </Grid>
                            <Grid item xs={6} lg={4}>
                                <FormControlLabel
                                    control={<Checkbox checked={!!customer.xBusiness} onChange={(e) => handleChange("xBusiness", e.target.checked)} />}
                                    label={t("common.business")}
                                />
                            </Grid>
                            <Grid item xs={6} lg={4}>
                                <FormControlLabel
                                    control={<Checkbox checked={!!customer.xTemplate} onChange={(e) => handleChange("xTemplate", e.target.checked)} />}
                                    label={t("common.template")}
                                />
                            </Grid>
                            {/* Toggle for Settings Section */}
                            <Grid item xs={12}>
                                <FormControlLabel
                                    control={<Switch checked={showSettings} onChange={(e) => setShowSettings(e.target.checked)} />}
                                    label={t("common.showAdvancedSettings")}
                                />
                            </Grid>

                            {/* Settings Section */}
                            {showSettings && (
                                <>
                                    <Box
                                        sx={{
                                            backgroundColor: "secondary.main",
                                            padding: 2,
                                            borderRadius: 2,
                                            width: "100%"
                                        }}
                                    >

                                        <Grid item xs={12}>
                                            <FormControl fullWidth margin="normal">
                                                <CustomerPriceGroupAutoComplete
                                                    key={customer.customerPriceGroup || null}
                                                    valueId={customer.customerPriceGroup || null}
                                                    onChange={(selected) => handleChange("customerPriceGroup", selected?.value || "")}
                                                />

                                            </FormControl>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <FormControl fullWidth margin="normal">
                                                <SalespersonAutoComplete
                                                    key={customer.salespersonCode || null}
                                                    valueId={customer.salespersonCode || null}
                                                    onChange={(selected) => handleChange("salespersonCode", selected?.value || "")}
                                                />
                                            </FormControl>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <FormControl fullWidth margin="normal">
                                                <CustomerInvoiceDiscountGroupAutoComplete
                                                    key={customer.invoiceDiscCode || null}
                                                    valueId={customer.invoiceDiscCode || null}
                                                    onChange={(selected) => handleChange("invoiceDiscCode", selected?.value || "")}
                                                />

                                            </FormControl>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <FormControl fullWidth margin="normal">
                                                <VatbusPostingGroupAutoComplete
                                                    key={customer.vatBusPostingGroup || null}
                                                    valueId={customer.vatBusPostingGroup || null}
                                                    onChange={(selected) => handleChange("vatBusPostingGroup", selected?.value || "")}
                                                />
                                            </FormControl>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <FormControl fullWidth margin="normal">
                                                <PaymentTermsAutoComplete
                                                    key={customer.paymentTermsCode || null}
                                                    valueId={customer.paymentTermsCode || null}
                                                    onChange={(selected) => handleChange("paymentTermsCode", selected?.value || "")}
                                                />


                                            </FormControl>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <FormControl fullWidth margin="normal">
                                                <ShipmentMethodAutoComplete
                                                    key={customer.shipmentMethodCode || null}
                                                    valueId={customer.shipmentMethodCode || null}
                                                    onChange={(selected) => handleChange("shipmentMethodCode", selected?.value || "")}
                                                />

                                            </FormControl>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <ShippingAgentServiceSelect
                                                shippingAgentCode={customer.shippingAgentCode}
                                                shippingServiceCode={customer.shippingServiceCode}
                                                onChange={(agent, service) =>
                                                    handleChanges({
                                                        shippingAgentCode: agent,
                                                        shippingServiceCode: service,
                                                    })
                                                }
                                                infoTextAgent={t("help.shippingAgent")}
                                                infoTextService={t("help.shippingService")}
                                                isLoading={isLoading}
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <FormControl fullWidth margin="normal">
                                                <Dimension1AutoComplete
                                                    key={customer.globalDimension1Code || null}
                                                    valueId={customer.globalDimension1Code || null}
                                                    onChange={(selected) => handleChange("globalDimension1Code", selected?.value || "")}
                                                />
                                            </FormControl>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <FormControl fullWidth margin="normal">
                                                <Dimension2AutoComplete
                                                    key={customer.globalDimension2Code || null}
                                                    valueId={customer.globalDimension2Code || null}
                                                    onChange={(selected) => handleChange("globalDimension2Code", selected?.value || "")}
                                                />
                                            </FormControl>
                                        </Grid>

                                    </Box>
                                </>
                            )}
                        </Grid>
                    </>
                )}

          <Box sx={{ marginTop: 2, textAlign: "center" }}>

            <Button variant="contained" onClick={handleSave} disabled={isLoading} sx={{ marginRight: 2 }}>
              {t("common.save")}
            </Button>
            <Button variant="outlined" onClick={goBack}>
              {t("common.back")}
            </Button>
          </Box>

            </CenteredCard>

        
        </>
    );
};

export default EditCustomerPage;
